import React from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import CampaignNewPage from "containers/admin/clients/campaign/campaign-new-page/campaign-new-page";
import { CampaignsPage } from "./campaigns-page";
import CampaignProvider from "containers/admin/clients/campaign/components/campaign-provider/campaign-provider";
import TouchpointPage from "containers/admin/clients/touchpoint/touchpoint-page";
import CampaignCreativePage from "containers/admin/clients/campaign/campaign-creative-page/campaign-creative-page";
import CreativeProvider from "containers/admin/clients/touchpoint/components/touchpoint-provider/touchpoint-provider";
import CampaignSettingsPage from "containers/admin/clients/campaign/campaign-settings-page/campaign-settings-page";
import CampaignSettingsEditTab from "containers/admin/clients/campaign/campaign-settings-page/campaign-settings-edit-tab/campaign-settings-edit-tab";
import CampaignStrategyPage from "containers/admin/clients/campaign/campaign-strategy-page/campaign-strategy-page";
import { hasCampaignTemplateInstance } from "containers/admin/clients/campaign/campaign-template-instance.utils";
import PageContent from "components/partials/page-content/page-content";
import FolderDetails from "containers/admin/clients/campaign/campaign-creative-page/folder-details/folder-details";

import { useCurrentCampaign } from "hooks/api/use-campaigns";

import { usePagePermission } from "hooks/use-page-permission";
import { usePermissionBlacklist } from "hooks/use-permission-blacklist";

import { getCampaignPaths, Route as AppRoute } from "utilities/app-routes";

import { Permission } from "types/auth";

const CampaignsIndexRoute = () => {
  const location = useLocation();
  const view = new URLSearchParams(location.search).get("view");

  if (view === "recent-creatives") {
    return <Navigate to={`../${AppRoute.recentCreatives}`} replace />;
  }

  return (
    <PageContent fixedContent>
      <CampaignsPage />
    </PageContent>
  );
};

const CampaignLayout = ({ children }: { children: React.ReactNode }) => {
  return <PageContent>{children}</PageContent>;
};

const CampaignCreativeLayout = ({ children }: { children: React.ReactNode }) => {
  return <PageContent fixedContent>{children}</PageContent>;
};

const CampaignDefaultTabRedirect = () => {
  const campaign = useCurrentCampaign();
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { campaignSettingsPath, campaignStrategyPath } = getCampaignPaths({ clientId, campaignId });

  if (hasCampaignTemplateInstance(campaign)) {
    return <Navigate to={campaignStrategyPath} replace />;
  }

  return <Navigate to={campaignSettingsPath} replace />;
};

const CampaignSettingsRoute = () => {
  const campaign = useCurrentCampaign();
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { campaignStrategyPath } = getCampaignPaths({ clientId, campaignId });

  if (hasCampaignTemplateInstance(campaign)) {
    return <Navigate to={campaignStrategyPath} replace />;
  }

  return (
    <CampaignLayout>
      <CampaignSettingsPage />
    </CampaignLayout>
  );
};

const CampaignSettingsEditRoute = () => {
  const campaign = useCurrentCampaign();
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { campaignStrategyPath } = getCampaignPaths({ clientId, campaignId });

  if (hasCampaignTemplateInstance(campaign)) {
    return <Navigate to={campaignStrategyPath} replace />;
  }

  return (
    <CampaignLayout>
      <CampaignSettingsEditTab />
    </CampaignLayout>
  );
};

const CampaignStrategyRoute = () => {
  const campaign = useCurrentCampaign();
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { campaignSettingsPath } = getCampaignPaths({ clientId, campaignId });

  if (!hasCampaignTemplateInstance(campaign)) {
    return <Navigate to={campaignSettingsPath} replace />;
  }

  return (
    <CampaignLayout>
      <CampaignStrategyPage />
    </CampaignLayout>
  );
};

export const CampaignsRoutes = () => {
  usePagePermission(Permission.PERM_CAMPAIGN_READ);
  usePermissionBlacklist([Permission.PERM_CLIENT_USER]);
  usePagePermission(Permission.PERM_TOUCHPOINT_READ);

  return (
    <Routes>
      <Route index element={<CampaignsIndexRoute />} />
      <Route path="new" element={<CampaignNewPage />} />
      <Route
        path=":campaignId"
        element={
          <CampaignProvider>
            <Outlet />
          </CampaignProvider>
        }>
        <Route index element={<CampaignDefaultTabRedirect />} />
        <Route path="settings" element={<CampaignSettingsRoute />} />
        <Route path="settings/edit" element={<CampaignSettingsEditRoute />} />
        <Route path="strategy" element={<CampaignStrategyRoute />} />
        <Route path="creative">
          <Route
            index
            element={
              <CampaignCreativeLayout>
                <CampaignCreativePage />
              </CampaignCreativeLayout>
            }
          />
          {/* Redirects for old URLs */}
          <Route path="emails" element={<Navigate to=".." replace />} />
          <Route path="landing-pages" element={<Navigate to=".." replace />} />
          <Route path="email/new" element={<Navigate to=".." replace />} />
          <Route path="landing-page/new" element={<Navigate to=".." replace />} />
          <Route
            path="folder/:folderId"
            element={
              <CampaignCreativeLayout>
                <FolderDetails />
              </CampaignCreativeLayout>
            }
          />
          <Route
            path=":touchpointId/*"
            element={
              <CreativeProvider>
                <TouchpointPage />
              </CreativeProvider>
            }
          />
        </Route>
        <Route path="*" element={<CampaignDefaultTabRedirect />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};
